import React, {
	useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import Cropper from 'cropperjs';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState } from '../../../stores/types/types';
import * as ModalActions from '../../../stores/actions/modal';
import * as SessionActions from '../../../stores/actions/session';
import { UploadProfileImage, UploadResponse } from '../../../connection/upload-image';
import { GetUploadEndpoint, UploadToS3 } from '../../../connection/media';
import { random } from '../../../utils/random';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { MODAL_AVATAR_ACTIONS } from '../../../stores/reducers/modal';
import { createMediaFile } from '../../../utils/media';
import { cleanupVideoStreamElement } from '../../../utils/video/video-streams';
import {IMG_TYPES} from "../../../types/constants";

const AVATAR_PHOTO_STATES = {
	UPLOAD: 'upload',
	EDIT: 'edit',
	CAMERA: 'camera',
};

interface Props {
	isOnboarding: boolean;
	modalAvatarAction: string;
}

export default function AvatarPhoto(props: Props) {
	const dispatch = useDispatch();
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const showroomsTokens = useSelector(
		(state: GreenroomState) => state.SessionReducer.showroomsTokens,
	);
	const mainVideoStream = useSelector(
		(state: GreenroomState) => state.VideoReducer.mainVideoStream,
	);
	const selectedChannel = useSelector(
		(state: GreenroomState) => state.HomeReducer.selectedChannel,
	);

	const {
		isOnboarding,
		modalAvatarAction,
	} = props;

	const fileInput = useRef<HTMLInputElement | null>(null);
	const imageElement = useRef<HTMLImageElement>(null);
	const cropper = useRef<Cropper | null>(null);
	const videoElement = useRef<HTMLVideoElement | null>(null);

	const [avatarPhotoState, setAvatarPhotoState] = useState<string>(AVATAR_PHOTO_STATES.UPLOAD);
	const [originalImage, setOriginalImage] = useState<string>('');
	const [originalImageName, setOriginalImageName] = useState<string>('');
	const [originalImageType, setOriginalImageType] = useState<string>('');
	const [zoomRatio, setZoomRatio] = useState<number>(1);
	const [minSliderZoom, setMinSliderZoom] = useState<number>(0);
	const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (imageElement.current && avatarPhotoState === AVATAR_PHOTO_STATES.EDIT) {
			cropper.current = new Cropper(imageElement.current, {
				guides: false,
				center: false,
				responsive: false,
				rotatable: false,
				zoomOnTouch: false,
				highlight: false,
				cropBoxResizable: false,
				cropBoxMovable: false,
				viewMode: 1,
				dragMode: 'move',
				autoCropArea: 1,
				ready: () => {
					if (!cropper.current) return;

					const cropperTop = document.getElementsByClassName('cropper-point point-n');
					const helperDiv = document.getElementById('crop-box-helper');
					if (cropperTop[0] && helperDiv) cropperTop[0].appendChild(helperDiv);

					const imageData = cropper.current.getImageData();
					const containerData = cropper.current.getContainerData();

					const minWidth = 240 / imageData.naturalWidth;
					const minHeight = 240 / imageData.naturalHeight;
					const minZoom = minHeight > minWidth ? minHeight : minWidth;

					cropper.current.zoomTo(minZoom);
					setMinSliderZoom(minZoom);

					cropper.current.setCropBoxData({
						left: (containerData.width - 240) / 2,
						top: (containerData.height - 240) / 2,
						width: 240,
						height: 240,
					});

					setZoomRatio(imageData.width / imageData.naturalWidth);
					setIsLoading(false);
				},
				zoom: (event: CustomEvent) => {
					if (cropper.current) {
						if (event.detail.ratio > 10) {
							event.preventDefault();
							cropper.current.zoomTo(10);
							setZoomRatio(10);
						} else {
							setZoomRatio(event.detail.ratio);
						}
					}
				},
			});
		}
	}, [avatarPhotoState]);

	function zoomWithSlider(event: React.ChangeEvent<HTMLInputElement>) {
		if (!cropper.current) return;

		cropper.current.zoomTo(event.target.valueAsNumber);
	}

	function checkFileInput(event: React.ChangeEvent<HTMLInputElement>) {
		if (event.target.files) {
			setIsLoading(true);
			const url = URL.createObjectURL(event.target.files[0]);
			setOriginalImage(url);
			setOriginalImageName(event.target.files[0].name.split('.')[0]);
			setOriginalImageType(event.target.files[0].type);
			setAvatarPhotoState(AVATAR_PHOTO_STATES.EDIT);
		}
	}

	function checkFileDrop(event: React.DragEvent<HTMLInputElement>) {
		event.preventDefault();
		setIsDraggingOver(false);
		const file = event.dataTransfer.files[0];
		const acceptedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
		if (acceptedTypes?.includes(file.type)) {
			setIsLoading(true);
			const url = URL.createObjectURL(file);
			setOriginalImage(url);
			setOriginalImageName(file.name.split('.')[0]);
			setOriginalImageType(file.type);
			setAvatarPhotoState(AVATAR_PHOTO_STATES.EDIT);
		}
	}

	async function uploadLogo(channelId: string | number, file: File) {
		const fileToUpload = createMediaFile(file);
		const prefix = `uploads/greenroom-channels/${channelId}`;
		const endpoint = await GetUploadEndpoint(token!, prefix, fileToUpload.name);
		await UploadToS3(endpoint.url, fileToUpload, endpoint.fields, fileToUpload.name);
		return `${endpoint.url}/${prefix}/${fileToUpload.name}`;
	}

	function uploadEditedImage() {
		if (!cropper.current || !user || !token) return;

		const canvas = cropper.current.getCroppedCanvas();
		canvas.toBlob((blob) => {
			const file = new File([blob as Blob], `${random(6)}${originalImageName}.${originalImageType.split('/')[1]}`, { type: originalImageType });
			if (modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR) {
				UploadProfileImage(token, file).then((res: UploadResponse) => {
					dispatch(SessionActions.setProfileInfo({ avatar: res.url }));
					const columns = {
						first_name: user.first_name,
						last_name: user.last_name,
						title: user.title,
						location: user.location,
						avatar: res.url,
					};
					dispatch(SessionActions.updateProfile(
						user.gr_user,
						appSignedInWith,
						columns,
						token,
						showroomsTokens?.accessToken,
					));
				});
			} else if (modalAvatarAction === MODAL_AVATAR_ACTIONS.CHANNEL_ICON && selectedChannel) {
				uploadLogo(selectedChannel.channel, file).then((res: string) => {
					dispatch(SessionActions.updateChannel(token, { ...selectedChannel, logo_white: res }));
				});
			}
			dispatch(ModalActions.setModalOpen(false));
		}, originalImageType);
	}

	function grayButtonClick() {
		if (avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA) {
			setAvatarPhotoState(AVATAR_PHOTO_STATES.UPLOAD);
		} else {
			setAvatarPhotoState(AVATAR_PHOTO_STATES.CAMERA);
		}
	}

	function greenButtonClick() {
		if (avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA) {
			if (!videoElement.current) return;

			setIsLoading(true);
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			canvas.width = videoElement.current?.videoWidth;
			canvas.height = videoElement.current?.videoHeight;

			if (isOnboarding || !host) {
				ctx?.translate(videoElement.current?.videoWidth, 0);
				ctx?.scale(-1, 1);
			}

			ctx?.drawImage(
				videoElement.current,
				0,
				0,
				videoElement.current?.videoWidth,
				videoElement.current?.videoHeight,
			);

			const frame = canvas.toDataURL('image/png');
			setOriginalImage(frame);
			setOriginalImageName(`camera_capture${random(10)}`);
			setOriginalImageType('image/png');
			setAvatarPhotoState(AVATAR_PHOTO_STATES.EDIT);
			canvas.remove();
		} else {
			uploadEditedImage();
		}
	}

	const actionText = useMemo(() => {
		switch (modalAvatarAction) {
			case MODAL_AVATAR_ACTIONS.AVATAR:
				return 'avatar';
			case MODAL_AVATAR_ACTIONS.CHANNEL_ICON:
				return 'channel icon';
			default:
				return '';
		}
	}, [modalAvatarAction]);

	const avatarHeaderText = useMemo(() => {
		switch (avatarPhotoState) {
			case AVATAR_PHOTO_STATES.UPLOAD:
				return `Change your ${actionText}`;
			case AVATAR_PHOTO_STATES.EDIT:
				return 'Edit photo';
			case AVATAR_PHOTO_STATES.CAMERA:
				return 'Take a photo';
			default:
				return '';
		}
	}, [avatarPhotoState, actionText]);

	return (
		<div className="info-modal avatar-photo">
			<div className="info-modal-header align-items-center justify-content-space-between">
				<h1>{avatarHeaderText}</h1>
				<button onClick={() => dispatch(ModalActions.setModalOpen(false))}>
					<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} />
				</button>
			</div>
			<div className="avatar-photo-modal-body width-full">
				{avatarPhotoState === AVATAR_PHOTO_STATES.UPLOAD && (
					<div className={classNames('upload-container', isDraggingOver && 'dragging-over')}
						onDragOver={(event: React.DragEvent<HTMLInputElement>) => event.preventDefault()}
						onDragEnter={(event: React.DragEvent<HTMLInputElement>) => {
							event.preventDefault();
							setIsDraggingOver(true);
						}}
						onDragLeave={(event: React.DragEvent<HTMLInputElement>) => {
							event.preventDefault();
							setIsDraggingOver(false);
						}}
						onDrop={checkFileDrop}
					>
						<button className="round padding-small background-lighter" onClick={() => fileInput.current?.click()}>
							<Icon name={ICONS.ADD} color={COLORS.WHITE} size={10} />
						</button>
						<span className="font-weight-normal">Drag & Drop to add your photo</span>
					</div>
				)}
				{avatarPhotoState === AVATAR_PHOTO_STATES.EDIT && (
					<>
						<div className="main-container">
							<img style={{ opacity: isLoading ? 0 : 1 }} ref={imageElement} src={originalImage} alt="Source" />
							<div id="crop-box-helper">
								<Icon name={ICONS.MOVE} color={COLORS.WHITE} size={12} />
								<span>Drag to Reposition</span>
							</div>
						</div>
						<div className="crop-slider">
							<Icon name={ICONS.ZOOM_OUT} color={COLORS.MEDIUM_GRAY} size={16} />
							<input
								type="range"
								onChange={zoomWithSlider}
								value={zoomRatio}
								step={0.01}
								min={minSliderZoom}
								max={10}
							/>
							<Icon name={ICONS.ZOOM_IN} color={COLORS.MEDIUM_GRAY} size={16} />
						</div>
					</>
				)}
				{avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA && (
					<div className="main-container">
						<video
							autoPlay
							playsInline
							style={{ transform: isOnboarding || !host ? 'scaleX(-1)' : 'none' }}
							ref={(ref) => {
								if (ref && ref.srcObject !== mainVideoStream) {
									try {
										ref.srcObject = mainVideoStream; // eslint-disable-line no-param-reassign
										ref.volume = 0; // eslint-disable-line no-param-reassign
										ref.play().catch((e) => console.error(e));
										videoElement.current = ref;
										cleanupVideoStreamElement(ref);
									} catch (e) {
										console.error(e);
									}
								}
							}}>
						</video>
					</div>
				)}
			</div>
			<div className="info-modal-bottom">
				<div className="info-modal-buttons">
					{modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR && (
						<button className={classNames('info-gray', avatarPhotoState === AVATAR_PHOTO_STATES.UPLOAD && 'upload')} onClick={grayButtonClick} disabled={!mainVideoStream}>
							{avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA ? 'Cancel' : 'Use camera'}
						</button>
					)}
					{avatarPhotoState !== AVATAR_PHOTO_STATES.UPLOAD && (
						<button className="info-green" onClick={greenButtonClick} disabled={false}>
							{avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA ? 'Take photo' : `Use as ${actionText}`}
						</button>
					)}
				</div>
				{avatarPhotoState === AVATAR_PHOTO_STATES.EDIT && (
					<button className="transparent height-full align-items-center margin-left-small" style={{ height: '48px' }}
						onClick={() => {
							if (fileInput.current) fileInput.current.value = '';
							setAvatarPhotoState(AVATAR_PHOTO_STATES.UPLOAD);
							setOriginalImage('');
						}}>
						<Icon name={ICONS.UPLOAD} color={COLORS.WHITE} size={16} />
						<span className="text-white font-weight-heavy margin-left-small">Replace photo</span>
					</button>
				)}
			</div>
			<input
				accept={IMG_TYPES
					.map((a) => `.${a}`)
					.join(',')
				}
				onChange={checkFileInput}
				ref={(ref) => {
					fileInput.current = ref;
				}}
				type="file"
				style={{ display: 'none' }}
			/>
		</div>
	);
}
